import { useCallback, useEffect, useState } from 'react'
import type { AccountRow, AccountsView, ApiClient, DiscoveredAccount, Template } from '../api/client'
import { t } from '../i18n/dict'
import { describeError, problem, type Problem } from '../i18n/errors'
import { ProblemLine } from '../ProblemLine'
import { daysUntil, money, pct } from '../i18n/format'
import { decimal, formats } from '../i18n/locale'
import { awaitRequest } from './requests'

/**
 * Which accounts you have, and whether the guardian is watching them. That is the whole job.
 *
 * It is deliberately NOT an account picker. cTrader already owns that choice — its own
 * selector decides what the ticket trades and what Rules, Report and Journal describe — so a
 * second selector here would be a second answer to a question that already has one. What this
 * screen adds is the part cTrader cannot know: guarded or not, and how to change that.
 *
 * Everything about HOW an account is guarded lives on the Rules screen. Repeating the limits
 * here is what made this a six-column table that did not fit the bottom sheet.
 */
export function Accounts({ api, nowMs, activeAccount }: { api: ApiClient; nowMs: number; activeAccount: number | null }) {
  const [view, setView] = useState<AccountsView | null>(null)
  const [discovered, setDiscovered] = useState<DiscoveredAccount[] | null>(null)
  const [error, setError] = useState<Problem | null>(null)
  const [guarding, setGuarding] = useState<number | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    api.accounts().then(setView).catch((e: unknown) => setError(describeError(e)))
    api.discovered().then(setDiscovered).catch(() => setDiscovered([]))
  }, [api])

  useEffect(load, [load])

  const refresh = async () => {
    setBusy(t('accounts.refreshing'))
    try {
      const submitted = await api.refreshAccounts()
      const result = await awaitRequest(api, submitted.id)
      // The daemon's own words, from the queue row. English by decision — see
      // docs/12-plugin-i18n.md §8; it is written minutes later, by a process with no request.
      setError(result.status === 'failed' ? problem(t('requests.failed'), result.error) : null)
    } finally {
      setBusy(null)
      load()
    }
  }

  if (error && !view) return <ProblemLine problem={error} />
  if (!view) return <p className="muted">{t('common.reading')}</p>

  const expires = view.authorisationExpiresAt ? daysUntil(view.authorisationExpiresAt, nowMs) : null
  const guardedById = new Map(view.accounts.map((a) => [a.account, a]))

  // Every account the consent reaches, plus any guarded one discovery has not seen yet — a
  // guarded account missing from this list would be the one omission that matters.
  // `| undefined` spelled out: the repo runs with exactOptionalPropertyTypes.
  const rows: { id: number; found: DiscoveredAccount | undefined; guarded: AccountRow | undefined }[] = (discovered ?? []).map((d) => ({
    id: d.accountId,
    found: d,
    guarded: guardedById.get(d.accountId),
  }))
  for (const a of view.accounts) {
    if (!rows.some((r) => r.id === a.account)) rows.push({ id: a.account, found: undefined, guarded: a })
  }
  rows.sort((x, y) => x.id - y.id)

  return (
    <section className="console">
      {rows.length === 0 ? (
        <div className="panel red">
          <strong>{t('accounts.none.title')}</strong>
          <p>{t('accounts.none.body')}</p>
        </div>
      ) : (
        <ul className="accounts">
          {rows.map((r) => (
            <li key={r.id} className={r.id === activeAccount ? 'account active' : 'account'}>
              <div className="who">
                <strong>{r.found?.broker || t('accounts.fallbackBroker')}</strong>{' '}
                {r.found?.isLive === undefined ? (
                  <span className="unknown">{t('common.unknown')}</span>
                ) : r.found.isLive ? (
                  <span className="live">{t('accounts.live')}</span>
                ) : (
                  <span className="demo">{t('accounts.demo')}</span>
                )}
                <small className="muted">
                  {r.found?.traderLogin
                    ? t('accounts.withLogin', { login: r.found.traderLogin, id: r.id })
                    : t('accounts.withoutLogin', { id: r.id })}
                  {r.id === activeAccount ? t('accounts.openInCtrader') : ''}
                </small>
              </div>
              <div className="state">
                {r.guarded === undefined ? (
                  <>
                    <span className="mode off">{t('accounts.notGuarded')}</span>
                    <button type="button" className="primary" onClick={() => setGuarding(r.id)} disabled={busy !== null}>
                      {t('accounts.guard')}
                    </button>
                  </>
                ) : r.guarded.enforcing === 0 ? (
                  <span className="mode observe">{t('accounts.observing', { count: r.guarded.rules })}</span>
                ) : (
                  <span className="mode enforce">
                    {t('accounts.enforcing', { enforcing: r.guarded.enforcing, rules: r.guarded.rules })}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className={expires !== null && expires < 0 ? 'bad' : 'muted'}>
        {expires === null
          ? t('accounts.auth.missing')
          : expires < 0
            ? t('accounts.auth.expired', { days: formats().integer.format(-expires) })
            : t('accounts.auth.valid', { days: formats().integer.format(expires) })}
      </p>

      {error && <ProblemLine problem={error} />}
      {busy && <p className="muted">{busy}</p>}

      <div className="actions">
        <button type="button" onClick={() => void refresh()} disabled={busy !== null}>
          {t('accounts.refresh')}
        </button>
      </div>

      {guarding !== null && (
        <Guard
          api={api}
          guarded={view.accounts.map((a) => a.account)}
          initial={guarding}
          onDone={() => {
            setGuarding(null)
            load()
          }}
        />
      )}
    </section>
  )
}

/**
 * The guard step: pick from what the consent reaches, choose a template. Every template starts
 * with all rules observing, so choosing wrong here costs a missed observation and never an
 * unintended trade. The leverage panel is the TUI's, word for word: it is the one control
 * software cannot provide.
 */
function Guard({ api, guarded, initial, onDone }: { api: ApiClient; guarded: number[]; initial: number; onDone: () => void }) {
  const [discovered, setDiscovered] = useState<DiscoveredAccount[] | null>(null)
  const [templates, setTemplates] = useState<Template[]>([])
  const [chosen, setChosen] = useState<DiscoveredAccount | null>(null)
  const [template, setTemplate] = useState<string>('')
  const [equity, setEquity] = useState<string>('')
  const [busy, setBusy] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Problem | null>(null)

  useEffect(() => {
    api
      .discovered()
      .then((list) => {
        setDiscovered(list)
        // They already said which account on the row they pressed; asking again would be
        // the second answer to a settled question.
        const picked = list.find((a) => a.accountId === initial)
        if (picked) {
          setChosen(picked)
          setEquity(picked.balance?.toString() ?? '')
        }
      })
      .catch(() => setDiscovered([]))
    api.templates().then(setTemplates).catch(() => setTemplates([]))
  }, [api, initial])

  const submit = async () => {
    if (!chosen || !template) return
    setBusy(t('guard.taking', { id: chosen.accountId }))
    try {
      const submitted = await api.guard(chosen.accountId, template, equity ? decimal(equity) : null)
      const result = await awaitRequest(api, submitted.id)
      setOutcome(
        result.status === 'failed' ? problem(t('requests.failed'), result.error) : problem(t('guard.done')),
      )
    } finally {
      setBusy(null)
    }
  }

  if (discovered === null) return <p className="muted">{t('guard.reading')}</p>

  if (discovered.length === 0) {
    return (
      <div className="panel red">
        <p>{t('guard.noneReachable')}</p>
        <button type="button" onClick={onDone}>
          {t('common.back')}
        </button>
      </div>
    )
  }

  if (outcome) {
    return (
      <div className="panel">
        <ProblemLine problem={outcome} className="" />
        <button type="button" onClick={onDone}>
          {t('common.ok')}
        </button>
      </div>
    )
  }

  const selectable = discovered.filter((a) => !guarded.includes(a.accountId))

  return (
    <div className="panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('guard.th.account')}</th>
              <th>{t('guard.th.kind')}</th>
              <th>{t('guard.th.login')}</th>
              <th>{t('guard.th.broker')}</th>
              <th>{t('guard.th.balance')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {discovered.map((a) => (
              <tr key={a.accountId} className={selectable.includes(a) ? 'row-link' : ''} onClick={() => selectable.includes(a) && (setChosen(a), setEquity(a.balance?.toString() ?? ''))}>
                <td>{a.accountId}</td>
                <td>
                  {a.isLive ? (
                    <span className="live">{t('accounts.live')}</span>
                  ) : (
                    <span className="demo">{t('accounts.demo')}</span>
                  )}
                </td>
                <td>{a.traderLogin ?? t('unit.none')}</td>
                <td>{a.broker}</td>
                <td>{a.balance === null ? t('unit.none') : money(a.balance)}</td>
                <td>
                  {guarded.includes(a.accountId) ? (
                    <span className="good">{t('guard.alreadyGuarded')}</span>
                  ) : chosen === a ? (
                    '◀'
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectable.length === 0 && <p className="good">{t('guard.allGuarded')}</p>}

      {chosen && (
        <>
          <dl className="grid">
            <dt>{t('guard.broker')}</dt>
            <dd>{chosen.broker}</dd>
            <dt>{t('guard.balance')}</dt>
            <dd>{chosen.balance === null ? t('common.unknown') : money(chosen.balance)}</dd>
            <dt>{t('guard.leverage')}</dt>
            <dd>{chosen.leverage === null ? t('common.unknown') : `1:${chosen.leverage}`}</dd>
          </dl>
          {chosen.leverage !== null && chosen.leverage >= 50 && (
            <div className="panel red">
              <strong>{t('guard.leverage.title')}</strong>
              <p>{t('guard.leverage.body', { leverage: chosen.leverage, move: pct(100 / chosen.leverage) })}</p>
              <p className="muted">{t('guard.leverage.note')}</p>
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('tmpl.th.template')}</th>
                  <th>{t('tmpl.th.risk')}</th>
                  <th>{t('tmpl.th.positions')}</th>
                  <th>{t('tmpl.th.openRisk')}</th>
                  <th>{t('tmpl.th.drawdown')}</th>
                  <th>{t('tmpl.th.hedging')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((row) => (
                  <tr key={row.name} className={template === row.name ? 'row-link on' : 'row-link'} onClick={() => setTemplate(row.name)}>
                    <td>{row.name}</td>
                    <td>{pct(row.maxRiskPercentPerTrade)}</td>
                    <td>{row.maxConcurrentPositions}</td>
                    <td>{pct(row.maxOpenRiskPercent)}</td>
                    <td>{pct(row.maxDrawdownPercent)}</td>
                    <td>{t(row.hedgingAllowed ? 'common.allowed' : 'common.blocked')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="inline">
            <span>{t('guard.equityQuestion')}</span>
            <input inputMode="decimal" value={equity} onChange={(e) => setEquity(e.target.value)} />
          </label>
        </>
      )}

      {busy && <p className="muted">{busy}</p>}
      <div className="actions">
        <button type="button" className="primary" disabled={!chosen || !template || busy !== null} onClick={() => void submit()}>
          {t('guard.submit', { id: chosen?.accountId ?? '' })}
        </button>
        <button type="button" onClick={onDone} disabled={busy !== null}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}
